'use client';

import { useEffect, useState } from 'react';
import type { ScheduleDto } from '@peach/types/schedule/schedule.dto.js';
import { guessTimezone } from '@/lib/utils/date';
import { weekLines } from '@/lib/availability/blocks';
import { useAvailabilityStore, useHasChanges } from '@/lib/stores/availability.store';
import { HoursEditor } from './HoursEditor';
import { EditButton, SaveCancel } from './EditActions';

const guard = (e: BeforeUnloadEvent) => e.preventDefault();

/**
 * The owner's week in the intro card, over the store Settings edits. Nothing is
 * sent until Save, so a refusal (an empty week under live products) answers a
 * press, not a pause mid-edit.
 */
export function OwnerHours({
  schedule,
  username,
}: {
  schedule: ScheduleDto | null;
  username: string;
}) {
  const initialize = useAvailabilityStore((s) => s.initialize);
  const setTimezone = useAvailabilityStore((s) => s.setTimezone);
  const saveSchedule = useAvailabilityStore((s) => s.saveSchedule);
  const saving = useAvailabilityStore((s) => s.saving);
  const generalError = useAvailabilityStore((s) => s.generalError);
  const setGeneralError = useAvailabilityStore((s) => s.setGeneralError);
  const seeded = useAvailabilityStore((s) => s.tutorUsername === username);
  const blocks = useAvailabilityStore((s) => s.blocks);
  const disabledDays = useAvailabilityStore((s) => s.disabledDays);
  const savedBlocks = useAvailabilityStore((s) => s.savedBlocks);
  const savedDisabledDays = useAvailabilityStore((s) => s.savedDisabledDays);
  const setWeek = useAvailabilityStore((s) => s.setWeek);
  const hasChanges = useHasChanges();
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    initialize(schedule, username);
    setTimezone(guessTimezone());
    // Seeded once: a refresh from the grid's save re-renders this page with a
    // new `schedule` object, and re-seeding would wipe a week being edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A pending week cannot be saved during unload, so the browser asks instead.
  useEffect(() => {
    if (!hasChanges) return;
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [hasChanges]);

  if (!seeded) return null;

  const save = async () => {
    if (await saveSchedule()) setEditing(false);
  };
  const cancel = () => {
    setWeek({ blocks: savedBlocks, disabledDays: savedDisabledDays });
    setGeneralError(null);
    setEditing(false);
  };

  if (editing) {
    return (
      <>
        <HoursEditor week={{ blocks, disabledDays }} onWeek={setWeek} idPrefix={`ab_${username}`} />
        <SaveCancel
          onSave={() => void save()}
          onCancel={cancel}
          saving={saving}
          error={generalError}
        />
      </>
    );
  }

  const lines = weekLines({ blocks: savedBlocks, disabledDays: savedDisabledDays });
  return (
    <>
      <div className="flex flex-col gap-0.5">
        {lines.length > 0 ? (
          lines.map((line) => (
            <span key={line} className="sf-num text-p-3 font-semibold text-[var(--sf-ink)]">
              {line}
            </span>
          ))
        ) : (
          <span className="text-p-3 font-semibold text-[var(--sf-mut)]">No open days</span>
        )}
      </div>
      <EditButton onClick={() => setEditing(true)} />
    </>
  );
}
